import { useEffect, useMemo } from 'react';

import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';

import { callGet } from '@/api/fetcher';
import { AgentCounts, Counts, DepartmentCounts } from '@/shared/types/chat';

export const endpoints = {
  counts: (deskId: string) => `/www/api/v1/desks/${deskId}/conversations/counts`,
  agentCounts: (deskId: string) => `/www/api/v1/desks/${deskId}/conversations/agent-counts`,
  departmentCounts: (deskId: string) =>
    `/www/api/v1/desks/${deskId}/conversations/department-counts`,
} as const;

// Events change the counts; the timer corrects drift, e.g. conversations that age out of 4 weeks.
const COUNTS_REFETCH_MS = 60 * 1000;

const countKeys = (deskId: string) => [
  [endpoints.counts(deskId)],
  [endpoints.agentCounts(deskId)],
  [endpoints.departmentCounts(deskId)],
];

export const invalidateCounts = (queryClient: QueryClient, deskId: string) => {
  countKeys(deskId).forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
};

export const isFetchingCounts = (queryClient: QueryClient, deskId: string) =>
  countKeys(deskId).some(
    (queryKey) => queryClient.getQueryState(queryKey)?.fetchStatus === 'fetching'
  );

const API = {
  listCounts: async (deskId: string): Promise<Counts> => callGet(endpoints.counts(deskId)),
  listAgentCounts: async (deskId: string): Promise<AgentCounts> =>
    callGet(endpoints.agentCounts(deskId)),
  listDepartmentCounts: async (deskId: string): Promise<DepartmentCounts> =>
    callGet(endpoints.departmentCounts(deskId)),
} as const;

export const useConversationsCounts = (deskId: string) => {
  const queryClient = useQueryClient();

  // Not `refetchInterval`: each cache write restarts that timer, so a busy desk would never refetch.
  useEffect(() => {
    if (!deskId) {
      return;
    }
    const timer = setInterval(() => {
      if (!document.hidden) {
        invalidateCounts(queryClient, deskId);
      }
    }, COUNTS_REFETCH_MS);
    return () => clearInterval(timer);
  }, [deskId, queryClient]);

  const { data: counts, status: countsStatus } = useQuery({
    queryKey: [endpoints.counts(deskId)],
    queryFn: () => API.listCounts(deskId),

    enabled: !!deskId,
    staleTime: 60 * 1000,
  });

  const { data: agentCounts, status: agentCountsStatus } = useQuery({
    queryKey: [endpoints.agentCounts(deskId)],
    queryFn: () => API.listAgentCounts(deskId),

    enabled: !!deskId,
    staleTime: 60 * 1000,
  });

  const { data: departmentCounts, status: departmentCountsStatus } = useQuery({
    queryKey: [endpoints.departmentCounts(deskId)],
    queryFn: () => API.listDepartmentCounts(deskId),

    enabled: !!deskId,
    staleTime: 60 * 1000,
  });

  const agentCountsSum = useMemo(
    () => agentCounts?.agents?.reduce((acc, agentCount) => acc + agentCount.open, 0),
    [agentCounts?.agents]
  );

  return {
    counts,
    countsStatus,
    agentCounts,
    agentCountsSum,
    agentCountsStatus,
    departmentCounts,
    departmentCountsStatus,
  };
};
